<template>
  <v-card
    :ripple="false"
    class="contract-card"
  >
    <v-card-text>
      <v-row class="text--secondary" dense>
        <v-col>
          <small> {{ $t('contract.name') }} </small>
          <p><b>{{contract.contractName}}</b></p>
        </v-col>
        <v-col @click.stop="currencyMenu = !currencyMenu">
          <v-menu
            ref="currencyMenu"
            v-model="currencyMenu"
            offset-y
            max-width="150px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :label="$tc('contract.currency', 1)"
                readonly
                v-on="on"
                outlined
                dense
                hide-details
                v-model="chosenCurrency.key"
                :append-icon="attrs['aria-expanded'] === 'true' ? 'mdi-menu-up' : 'mdi-menu-down'"
              />
            </template>
            <v-list width="100">
              <v-list-item
                v-for="(item, index) in contract.currencies"
                :key="index"
                @click="handleChangeCurrency(item)"
              >
                <v-list-item-title>{{ item.key }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="6">
          <div>
            <small> {{ $tc('contract.contractor', 1) }} </small>
            <span class="text--secondary">{{contract.contractor.companyName}}</span>
          </div>
          <div>
            <small> {{ $tc('contract.employer', 1) }} </small>
            <span class="text--secondary">{{contract.employer.companyName}}</span>
          </div>
        </v-col>
        <v-col cols="6">
          <div>
            <small> {{ $tc('contract.area', 1) }} </small>
            <span class="text--secondary">{{contract.area.name}}</span>
          </div>
          <div>
            <small> {{ $tc('contract.type', 1) }} </small>
            <span class="text--secondary">{{contract.contractType}}</span>
          </div>
          <div>
            <small> {{ $tc('contract.status', 1) }} </small>
            <span class="text--secondary">{{contract.active ? $t('contract.active') : $t('contract.inactive')}}</span>
            <v-badge
              bordered
              dot
              inline
              floating
              :class="contract.active ? 'glow-badge-success' : 'glow-badge-error'"
              :color="contract.active ? 'success' : 'error'"
            ></v-badge>
          </div>
        </v-col>
        <v-col cols="12">
          <div>
            <small> {{ $tc('contract.package', 1) }} </small>
            <span class="text--secondary">{{contract.package}}</span>
          </div>
          <div>
            <small> {{ $tc(`contract.project.${contract.area.code || 'OFF'}`, 1) }} </small>
            <span class="text--secondary">{{projectName}}</span>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text class="pt-0">
      <v-row class="mx-0" justify="start" align="end" v-if="partsButtons.length > 1">
        <v-chip-group
          v-model="selectedPart"
          column
        >
          <v-chip
            v-for="(part, index) in partsButtons"
            :key="index"
            x-small
            dark
            rounded
            :class="index === selectedPart ? 'white--text' : ''"
            :color="getDarkColor(index)"
            :outlined="index !== selectedPart"
          >
            {{ part.name }}
          </v-chip>
        </v-chip-group>
      </v-row>
      <v-row justify="start" align="start">
        <v-col>
          <v-btn
            x-small
            rounded
            dark
            :color="getDarkColor(selectedPart)"
            :outlined="!showVariations"
            @click.stop="showVariations = true"
            :style="{ marginLeft: '0px' }"
          >
            {{ $t('contract.stats.variations') }}
          </v-btn>
          <v-btn
            x-small
            rounded
            dark
            :color="getDarkColor(selectedPart)"
            :outlined="showVariations"
            @click.stop="showVariations = false"
          >
            {{ $t('contract.stats.payments') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="showVariations">
        <v-col
          cols="6"
          v-for="(group, index) in pieChartData"
          :key="index"
        >
          <chart-pie
            :headline="group.headline"
            :items="group.data"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="!showVariations"
        justify="end"
        align-content="end"
        class="my-2 mx-0"
      >
        <v-btn
          x-small
          rounded
          dark
          :color="getDarkColor(selectedPart)"
          :outlined="!showAdjusted"
          @click.stop="showAdjusted = true"
        >
          {{ $t('contract.stats.adjusted') }}
        </v-btn>
        <v-btn
          x-small
          rounded
          dark
          :color="getDarkColor(selectedPart)"
          :outlined="showAdjusted"
          @click.stop="showAdjusted = false"
        >
          {{ $t('contract.stats.forecasted') }}
        </v-btn>
      </v-row>
      <v-row v-if="!showVariations">
        <v-row v-if="showAdjusted">
          <v-col :col=15>
            <chart-bars
              v-for="(block, index) in adjustedBarsData"
              :key="index"
              :type="block.type"
              :entry="block"
              :labels="block.labels"
              :color="getDarkColor(selectedPart)"
            />
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col :col=15>
            <chart-bars
              v-for="(block, index) in forecastedBarsData"
              :key="index"
              :type="block.type"
              :entry="block"
              :labels="block.labels"
              :color="getDarkColor(selectedPart)"
            />
          </v-col>
        </v-row>
      </v-row>
    </v-card-text>
    <v-card-text class="mt-1 d-flex justify-center align-center">
      <v-btn
        rounded
        dark
        dense
        :color="getDarkColor(selectedPart)"
        @click="handleContractClick(contract)"
      >
        {{ $t('contract.selectThis') }}
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script>
import ColorsMixin from '@/mixins/ColorsMixin'
import ContractCardMixin from '@/mixins/ContractCardMixin'

export default {
  name: 'ContractCard',
  created () {
    this.chosenCurrency = this.contract.chosenCurrency
  },
  props: {
    contract: {
      type: Object,
      default: () => ({})
    }
  },
  mixins: [ColorsMixin, ContractCardMixin],
  data () {
    return {}
  },
  methods: {
    handleChangeCurrency (item) {
      this.chosenCurrency = item
    }
  },
  computed: {
    projectName () {
      return this.contract.project
        ? `${this.contract.project.name} ${this.contract.project.code ? `- ${this.contract.project.code}` : ''}`
        : ''
    }
  }
}
</script>

<style>
.contract-card {
  padding: 5px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.contract-card span {
  font-weight: bolder;
  margin-bottom: 0;
}
.contract-card .v-btn {
  margin-left: 10px;
}
</style>
